/**
 * Hash-based router. Nine route entries total. Round B fills content for
 * `weeks`, `week`, `article`, `changelog` + the new `more` parent route;
 * Round A renders the route shell and the full daily view (`daily` +
 * `dailyByDate`).
 *
 * Routing rules
 * ─────────────
 * `#/daily`                  → daily             (latest 7 days)
 * `#/daily/<date>`           → dailyByDate       (single Beijing calendar day)
 * `#/weeks`                  → weeks             (archive list)
 * `#/week/<id>`              → week              (one weekly report)
 * `#/article/<pmid>`         → article           (single article detail)
 * `#/changelog`              → changelog         (latest workflow runs)
 * `#/more`                   → more              (parent — default child: weeks)
 * `#/more/changelog`         → more              (parent — child: changelog)
 * `<anything else>`          → notFound
 *
 * The router pushes route changes into the central `store`; views read
 * `store.state.route` and re-render via `store.on("change", cb)`.
 *
 * Hash parsing is intentionally liberal: empty hash (`""` or `"#"`)
 * maps to `daily`, so first-load lands on the daily feed without
 * explicit `navigate`.
 */

import { setRoute } from "./store";
import type { Route, RouteName } from "./store";

export const ROUTE_PATTERNS: ReadonlyArray<{
  name: RouteName;
  pattern: RegExp;
  keys: string[];
}> = [
  { name: "daily", pattern: /^$|^[\/#]?$/u, keys: [] },
  { name: "daily", pattern: /^\/?daily\/?$/u, keys: [] },
  { name: "dailyByDate", pattern: /^\/?daily\/([^/]+)$/u, keys: ["date"] },
  { name: "weeks", pattern: /^\/?weeks\/?$/u, keys: [] },
  { name: "week", pattern: /^\/?week\/([^/]+)$/u, keys: ["id"] },
  { name: "article", pattern: /^\/?article\/([^/]+)$/u, keys: ["pmid"] },
  { name: "changelog", pattern: /^\/?changelog\/?$/u, keys: [] },
  { name: "more", pattern: /^\/?more\/?$/u, keys: [] },
  { name: "more", pattern: /^\/?more\/(changelog)\/?$/u, keys: ["child"] },
];

export function parseHash(raw?: string): Route {
  const hash = raw ?? (typeof window !== "undefined" ? window.location.hash : "");
  // The browser-native hash always starts with "#"; the parser accepts
  // both shapes so unit tests can pass either.
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  // Normalize: ensure single leading slash, no trailing slash. The
  // browser sometimes presents the hash with neither — both are
  // accepted and produce identical routes.
  const slash = stripped.startsWith("/") ? stripped : stripped ? `/${stripped}` : "";
  const [pathOnly, queryOnly] = slash.split("?");
  const path = pathOnly ?? "";

  for (const route of ROUTE_PATTERNS) {
    const m = path.match(route.pattern);
    if (m) {
      const params: Record<string, string> = {};
      route.keys.forEach((key, i) => {
        const value = m[i + 1];
        if (value !== undefined) params[key] = decodeURIComponent(value);
      });
      const query = parseQuery(queryOnly ?? "");
      return { name: route.name, params, query };
    }
  }
  return { name: "notFound", params: {}, query: parseQuery(queryOnly ?? "") };
}

export function navigate(path: string): void {
  if (typeof window === "undefined") return;
  const next = path.startsWith("#") ? path : `#${path}`;
  if (window.location.hash === next) {
    // Manually invoke — `hashchange` does not fire when hash doesn't
    // actually change.
    setRoute(parseHash(next));
    return;
  }
  window.location.hash = next;
}

export function startRouter(): void {
  if (typeof window === "undefined") return;
  const apply = (): void => setRoute(parseHash(window.location.hash));
  window.addEventListener("hashchange", apply);
  // Initial route — empty hash is legal and means "daily".
  if (window.location.hash === "" || window.location.hash === "#") {
    setRoute({ name: "daily", params: {}, query: {} });
  } else {
    setRoute(parseHash(window.location.hash));
  }
}

function parseQuery(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw) return out;
  for (const part of raw.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    if (eq === -1) {
      out[decodeURIComponent(part)] = "";
    } else {
      const k = decodeURIComponent(part.slice(0, eq));
      const v = decodeURIComponent(part.slice(eq + 1));
      out[k] = v;
    }
  }
  return out;
}
